import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { apiService, RealtimeAlertLevel, RealtimeAlertStatsPage, RealtimeAlertStatsRequest, RealtimeSearchRequest } from '../../services/api';
import { REALTIME_ALERT_LABELS, REALTIME_ALERT_LEVELS } from '../../features/realtime/config';
import { getRealtimeErrorMessage } from '../../features/realtime/errors';
import CustomSelect from '../CustomSelect';
import './RealtimeAlertDashboard.css';

type Preset = 'all' | 'hour' | 'day' | 'week' | 'custom';
type Range = { preset: Preset; from_at?: string; to_at?: string };
const RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: 'hour', label: 'Last hour' },
  { value: 'day', label: 'Last 24 hours' },
  { value: 'week', label: 'Last 7 days' },
  { value: 'custom', label: 'Custom range' },
];
const HOURS: Partial<Record<Preset, number>> = { hour: 1, day: 24, week: 168 };
const emptyCounts = (): Record<RealtimeAlertLevel, number> => ({ clean: 0, suspicious: 0, attention: 0, critical: 0 });
interface Props {
  projectId: string;
  feedId?: string;
  refreshKey?: number;
  onExplore: (request: RealtimeSearchRequest) => void;
}
interface Stats {
  scopeKey: string;
  counts: Record<RealtimeAlertLevel, number>;
  total: number;
  scope: RealtimeAlertStatsRequest;
}

const RealtimeAlertDashboard: React.FC<Props> = ({ projectId, feedId, refreshKey = 0, onExplore }) => {
  const [preset, setPreset] = useState<Preset>('all');
  const [range, setRange] = useState<Range>({ preset: 'all' });
  const [fromDraft, setFromDraft] = useState('');
  const [toDraft, setToDraft] = useState('');
  const [matchingOnly, setMatchingOnly] = useState(false);
  const [revision, setRevision] = useState(0);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const scopeKey = JSON.stringify([projectId, feedId, range, matchingOnly, refreshKey, revision]);
  const visibleStats = stats?.scopeKey === scopeKey ? stats : null;
  const fromDate = fromDraft ? new Date(fromDraft) : null;
  const toDate = toDraft ? new Date(toDraft) : null;
  const invalidDates = (fromDate && !Number.isFinite(fromDate.getTime())) || (toDate && !Number.isFinite(toDate.getTime()));
  const reversedDates = fromDate && toDate && fromDate.getTime() > toDate.getTime();
  const rangeError = invalidDates ? 'Choose a valid date and time.' : reversedDates ? 'Start time must be before end time.' : '';

  useEffect(() => {
    const controller = new AbortController();
    const hours = HOURS[range.preset];
    const scope: RealtimeAlertStatsRequest = {
      ...(feedId ? { feed_ids: [feedId] } : {}),
      matching_only: matchingOnly,
      ...(hours ? { from_at: new Date(Date.now() - hours * 3600000).toISOString() } : range.preset === 'custom' ? { ...(range.from_at ? { from_at: range.from_at } : {}), ...(range.to_at ? { to_at: range.to_at } : {}) } : {}),
    };
    setLoading(true); setError(''); setStats(null);
    void (async () => {
      const counts = emptyCounts();
      let total = 0;
      let cursor: string | null = null;
      do {
        const page: RealtimeAlertStatsPage = await apiService.getRealtimeAlertStats(projectId, { ...scope, ...(cursor ? { cursor } : {}) }, controller.signal);
        if (controller.signal.aborted) return;
        for (const level of REALTIME_ALERT_LEVELS) counts[level] += page.counts[level];
        total += page.total;
        cursor = page.next_cursor;
      } while (cursor);
      setStats({ scopeKey, scope, counts, total });
    })().catch(failure => {
      if (!controller.signal.aborted) setError(getRealtimeErrorMessage(failure, 'Unable to load alert totals. Please try again.'));
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [projectId, feedId, range, matchingOnly, scopeKey]);

  const choosePreset = (value: string) => {
    const next = value as Preset;
    setPreset(next);
    if (next !== 'custom') setRange({ preset: next });
  };
  const applyRange = (event: React.FormEvent) => {
    event.preventDefault();
    if (rangeError || (!fromDate && !toDate)) return;
    setRange({ preset: 'custom', ...(fromDate ? { from_at: fromDate.toISOString() } : {}), ...(toDate ? { to_at: toDate.toISOString() } : {}) });
  };
  const explore = (level?: RealtimeAlertLevel) => {
    if (!visibleStats || loading) return;
    onExplore({ mode: 'conditions', ...visibleStats.scope, ...(level ? { alert_levels: [level] } : {}), page_size: 50 });
  };
  const rangeLabel = range.preset === 'custom'
    ? `${range.from_at ? new Date(range.from_at).toLocaleString() : 'Beginning'} – ${range.to_at ? new Date(range.to_at).toLocaleString() : 'Now'}`
    : RANGE_OPTIONS.find(option => option.value === range.preset)?.label;
  return <section className="realtime-alert-dashboard" aria-label="Alert overview" aria-busy={loading}>
    <header><div><h3>Alert overview</h3><p>{rangeLabel} · {matchingOnly ? 'Engine matches' : 'All observations'}</p></div><button type="button" className="action-button" aria-label="Refresh alert overview" disabled={loading} onClick={() => setRevision(value => value + 1)}><RefreshCw size={14} /></button></header>
    <div className="realtime-alert-dashboard-controls"><CustomSelect value={preset} options={RANGE_OPTIONS} onChange={choosePreset} ariaLabel="Alert time range" /><label><input type="checkbox" checked={matchingOnly} onChange={event => setMatchingOnly(event.target.checked)} /> Only engine matches</label></div>
    {preset === 'custom' && <form className="realtime-alert-custom-range" onSubmit={applyRange}>
      <label className="form-group">From<input type="datetime-local" className="form-input" aria-label="Alert range start" value={fromDraft} onChange={event => setFromDraft(event.target.value)} /></label>
      <label className="form-group">To<input type="datetime-local" className="form-input" aria-label="Alert range end" value={toDraft} onChange={event => setToDraft(event.target.value)} /></label>
      <button type="submit" className="action-button" disabled={Boolean(rangeError) || (!fromDraft && !toDraft)}>Apply range</button>
      {rangeError && <p className="realtime-alert-range-error" role="status">{rangeError}</p>}
    </form>}
    {loading ? <div className="realtime-alert-dashboard-loading" role="status"><Loader2 size={16} className="realtime-spin" /> Counting observations…</div> : error ? <div className="realtime-alert-dashboard-error" role="alert"><span>{error}</span><button type="button" className="action-button" onClick={() => setRevision(value => value + 1)}>Try again</button></div> : visibleStats && <>
      <div className="realtime-alert-totals">
        <button type="button" className="realtime-alert-count realtime-alert-total" onClick={() => explore()} aria-label="Explore all observations"><span>Total observations</span><strong>{visibleStats.total.toLocaleString()}</strong></button>
        {REALTIME_ALERT_LEVELS.map(level => <button key={level} type="button" className={`realtime-alert-count realtime-alert-${level}`} onClick={() => explore(level)} aria-label={`Explore ${REALTIME_ALERT_LABELS[level]} observations`}><span><i aria-hidden="true" />{REALTIME_ALERT_LABELS[level]}</span><strong>{visibleStats.counts[level].toLocaleString()}</strong><small>{visibleStats.total ? Math.round(100 * visibleStats.counts[level] / visibleStats.total) : 0}%</small></button>)}
      </div>
      <div className="realtime-alert-distribution" role="img" aria-label={`Alert distribution: ${REALTIME_ALERT_LEVELS.map(level => `${REALTIME_ALERT_LABELS[level]} ${visibleStats.counts[level]}`).join(', ')}`}>
        {REALTIME_ALERT_LEVELS.map(level => <span key={level} className={`realtime-alert-${level}`} style={{ width: `${visibleStats.total ? 100 * visibleStats.counts[level] / visibleStats.total : 0}%` }} />)}
      </div>
      {!visibleStats.total && <p className="realtime-alert-dashboard-empty">No observations in this range.</p>}
    </>}
  </section>;
};

export default RealtimeAlertDashboard;
